import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import LazyLoad from "react-lazyload";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faXmark,
  faChevronLeft,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";
import "./Gallery.scss";
import { useLanguage } from "../../i18n/LanguageContext";
import { footer, gallery } from "../../i18n/content";
import { fetchGalleryYears } from "../../lib/galleryApi";
import { urlForImage } from "../../lib/sanityClient";

const Gallery = () => {
  const { t } = useLanguage();
  const [years, setYears] = useState(null);
  const [activeYear, setActiveYear] = useState("all");
  const [activeIndex, setActiveIndex] = useState(null);

  useEffect(() => {
    let active = true;
    fetchGalleryYears()
      .then((data) => {
        if (active) setYears(data);
      })
      .catch(() => {
        if (active) setYears([]);
      });
    return () => {
      active = false;
    };
  }, []);

  const imageList = useMemo(() => {
    if (!years) return [];
    const filtered =
      activeYear === "all" ? years : years.filter((y) => y.year === activeYear);
    const winners = filtered.flatMap((y) =>
      (y.winnerPhotos || []).map((photo, i) => ({
        key: `${y.year}-w-${i}`,
        year: y.year,
        isWinner: true,
        thumb: urlForImage(photo, { width: 500, height: 667 }),
        full: urlForImage(photo, { width: 1600 }),
      }))
    );
    const general = filtered.flatMap((y) =>
      (y.photos || []).map((photo, i) => ({
        key: `${y.year}-${i}`,
        year: y.year,
        isWinner: false,
        thumb: urlForImage(photo, { width: 500, height: 667 }),
        full: urlForImage(photo, { width: 1600 }),
      }))
    );
    return [...winners, ...general];
  }, [years, activeYear]);

  const winnerImages = useMemo(
    () =>
      imageList
        .map((img, index) => ({ ...img, index }))
        .filter((img) => img.isWinner),
    [imageList]
  );
  const generalImages = useMemo(
    () =>
      imageList
        .map((img, index) => ({ ...img, index }))
        .filter((img) => !img.isWinner),
    [imageList]
  );

  const isOpen = activeIndex !== null;

  const close = useCallback(() => setActiveIndex(null), []);
  const showPrev = useCallback(
    () =>
      setActiveIndex((i) => (i === 0 ? imageList.length - 1 : i - 1)),
    [imageList.length]
  );
  const showNext = useCallback(
    () =>
      setActiveIndex((i) => (i === imageList.length - 1 ? 0 : i + 1)),
    [imageList.length]
  );

  useEffect(() => {
    if (!isOpen) return;

    document.body.style.overflow = "hidden";
    const onKeyDown = (e) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowLeft") showPrev();
      if (e.key === "ArrowRight") showNext();
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [isOpen, close, showPrev, showNext]);

  return (
    <div className="gallery_test_wrapper">
      <header className="lw-page-hero">
        <div className="lw-wrap">
          <div className="lw-eyebrow">
            <span className="lw-dot" />
            {t(gallery.eyebrow)}
          </div>
          <h1>{t(gallery.title)}</h1>
          <p>{t(gallery.lede)}</p>
        </div>
      </header>

      <section className="lwt-gallery-section">
        <div className="lw-wrap">
          {years && years.length > 0 && (
            <div className="lwt-year-filter">
              <button
                type="button"
                className={activeYear === "all" ? "active" : ""}
                onClick={() => setActiveYear("all")}
              >
                {t(gallery.allYears)}
              </button>
              {years.map((y) => (
                <button
                  type="button"
                  key={y.year}
                  className={activeYear === y.year ? "active" : ""}
                  onClick={() => setActiveYear(y.year)}
                >
                  {y.year}
                </button>
              ))}
            </div>
          )}

          {!years && <p className="lwt-gallery-status">{t(gallery.loading)}</p>}

          {activeYear !== "all" && winnerImages.length > 0 && (
            <div className="lwt-gallery-winners">
              <div className="lw-section-head">
                <span className="lw-kicker">{t(gallery.winnersKicker)}</span>
                <h2 className="lw-h-title">{t(gallery.winnersTitle)}</h2>
              </div>
              <div className="lwt-gallery-grid">
                {winnerImages.map((img) => (
                  <LazyLoad key={img.key} height={220} offset={100}>
                    <button
                      type="button"
                      className="lw-reel-card lwt-gallery-card"
                      onClick={() => setActiveIndex(img.index)}
                      aria-label={`${t(gallery.openImage)} ${img.index + 1}/${
                        imageList.length
                      }`}
                    >
                      <img src={img.thumb} alt="" />
                      <div className="lw-reel-overlay" />
                      <div className="lw-rec">
                        <span className="lw-rec-dot" />
                        REC
                      </div>
                      <div className="lw-tag">
                        <span>{img.year}</span>
                      </div>
                    </button>
                  </LazyLoad>
                ))}
              </div>
            </div>
          )}

          <div className="lwt-gallery-grid">
            {generalImages.map((img) => (
              <LazyLoad key={img.key} height={220} offset={100}>
                <button
                  type="button"
                  className="lw-reel-card lwt-gallery-card"
                  onClick={() => setActiveIndex(img.index)}
                  aria-label={`${t(gallery.openImage)} ${img.index + 1}/${
                    imageList.length
                  }`}
                >
                  <img src={img.thumb} alt="" />
                  <div className="lw-reel-overlay" />
                  <div className="lw-rec">
                    <span className="lw-rec-dot" />
                    REC
                  </div>
                  <div className="lw-tag">
                    <span>{img.year}</span>
                  </div>
                </button>
              </LazyLoad>
            ))}
          </div>
        </div>
      </section>

      <footer className="lw-footer">
        <div className="lw-wrap lw-foot-bottom">
          <span>{t(footer.est)}</span>
          <Link to="/">{t(footer.backHome)}</Link>
        </div>
      </footer>

      {isOpen && (
        <div
          className="lwt-lightbox"
          role="dialog"
          aria-modal="true"
          aria-label={t(gallery.viewerLabel)}
          onClick={close}
        >
          <button
            type="button"
            className="lwt-lb-close"
            onClick={close}
            aria-label={t(gallery.close)}
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>

          <button
            type="button"
            className="lwt-lb-nav lwt-lb-prev"
            onClick={(e) => {
              e.stopPropagation();
              showPrev();
            }}
            aria-label={t(gallery.prev)}
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>

          <div className="lwt-lb-stage" onClick={(e) => e.stopPropagation()}>
            <img src={imageList[activeIndex].full} alt="" />
            <div className="lwt-lb-tag">
              <span className="lwt-lb-rec">
                <span className="lw-rec-dot" />
                {imageList[activeIndex].year}
              </span>
              <span>
                {String(activeIndex + 1).padStart(2, "0")} /{" "}
                {String(imageList.length).padStart(2, "0")}
              </span>
            </div>
          </div>

          <button
            type="button"
            className="lwt-lb-nav lwt-lb-next"
            onClick={(e) => {
              e.stopPropagation();
              showNext();
            }}
            aria-label={t(gallery.next)}
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
        </div>
      )}
    </div>
  );
};

export default Gallery;
